import { sanitizeHtml } from "../utils/sanitize";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog.jsx';
import { Progress } from '../components/ui/progress';
import { Mail, Plus, RefreshCw, Trash2, CheckCircle, XCircle, Clock, Search, ChevronLeft, ChevronRight, ArrowUpDown, Paperclip, Ticket, ExternalLink, Loader2, Settings, Calendar, Activity, AlertCircle, Bell, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { formatInCST, formatWithTimezone } from '../utils/timezone';
import { deriveMailboxHealth } from '../utils/mailboxHealth';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const MailboxLastRun = ({ mailbox }) => {
  const failed = mailbox.last_sync_status === 'error' || Boolean(mailbox.last_sync_error);
  const outcome = failed ? 'Failed' : mailbox.last_sync_status === 'success' ? 'Succeeded' : 'Outcome not recorded';
  const hasRun = mailbox.last_sync_at || mailbox.last_sync_status || mailbox.last_sync_error;
  return (
    <div className="space-y-1 min-w-[180px] max-w-sm text-xs" data-testid={`mailbox-last-run-${mailbox.id}`}>
      <p className={failed ? 'font-medium text-red-600 dark:text-red-400' : 'font-medium'}>{outcome}</p>
      {mailbox.last_sync_at ? (
        <p className="text-muted-foreground">{formatWithTimezone(mailbox.last_sync_at, 'MMM d, h:mm a')}</p>
      ) : hasRun ? (
        <p className="text-muted-foreground">Run time not recorded</p>
      ) : mailbox.last_synced ? (
        <p className="text-muted-foreground">Saved sync time: {formatWithTimezone(mailbox.last_synced, 'MMM d, h:mm a')}</p>
      ) : (
        <p className="text-muted-foreground">No recorded runs</p>
      )}
      {mailbox.last_sync_error && <p className="break-words text-red-600 dark:text-red-400">{mailbox.last_sync_error}</p>}
    </div>
  );
};

// Outlook-style Email Viewer Component using iframe for proper HTML rendering
const EmailViewer = ({ email }) => {
  const iframeRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  // Detect if email HTML contains external images
  const hasExternalImages = React.useMemo(() => {
    if (!email?.body_html) return false;
    // Check for img tags with http(s) src, background-image urls, or cid: references
    return /<img[^>]+src\s*=\s*["']https?:\/\//i.test(email.body_html) ||
           /background(-image)?\s*:\s*url\s*\(\s*["']?https?:\/\//i.test(email.body_html) ||
           /<img[^>]+src\s*=\s*["']cid:/i.test(email.body_html);
  }, [email?.body_html]);
  
  useEffect(() => {
    if (iframeRef.current && email?.body_html) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      
      // When images are NOT loaded, replace img src with placeholder to block loading
      let bodyHtml = email.body_html;
      if (!imagesLoaded) {
        // Replace img src with data attribute to prevent loading
        bodyHtml = bodyHtml.replace(
          /<img([^>]*)\ssrc\s*=\s*["'](https?:\/\/[^"']+)["']/gi,
          '<img$1 data-blocked-src="$2" src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'80\' viewBox=\'0 0 120 80\'%3E%3Crect fill=\'%23f0f0f0\' width=\'120\' height=\'80\'/%3E%3Ctext x=\'50%25\' y=\'50%25\' font-family=\'sans-serif\' font-size=\'11\' fill=\'%23999\' text-anchor=\'middle\' dominant-baseline=\'middle\'%3EImage blocked%3C/text%3E%3C/svg%3E" style="border:1px dashed #ccc;border-radius:4px;"'
        );
      }
      // Always neutralize unresolved cid: images — browsers cannot load the cid: scheme
      // (these are embedded images that were not resolved to data URIs during import).
      // Leaving them causes net::ERR_UNKNOWN_URL_SCHEME console errors.
      bodyHtml = bodyHtml.replace(
        /<img([^>]*)\ssrc\s*=\s*["']cid:[^"']+["']/gi,
        '<img$1 src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'80\' viewBox=\'0 0 120 80\'%3E%3Crect fill=\'%23f7f7f7\' width=\'120\' height=\'80\'/%3E%3Ctext x=\'50%25\' y=\'50%25\' font-family=\'sans-serif\' font-size=\'11\' fill=\'%23999\' text-anchor=\'middle\' dominant-baseline=\'middle\'%3EEmbedded image%3C/text%3E%3C/svg%3E" style="border:1px dashed #ccc;border-radius:4px;max-width:200px;"'
      );
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            * { box-sizing: border-box; }
            html, body {
              margin: 0;
              padding: 0;
              font-family: 'Segoe UI', Calibri, Arial, Helvetica, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              color: #333333;
              background-color: #ffffff;
            }
            body { padding: 16px; }
            p { margin: 0 0 1em 0; }
            h1, h2, h3, h4, h5, h6 {
              margin: 0 0 0.5em 0;
              font-weight: 600;
              color: #1a1a1a;
            }
            a { color: #0078d4; text-decoration: none; }
            a:hover { text-decoration: underline; }
            img { max-width: 100%; height: auto; display: block; }
            table { border-collapse: collapse; max-width: 100%; }
            td, th { padding: 8px; text-align: left; vertical-align: top; }
            blockquote {
              margin: 1em 0;
              padding-left: 16px;
              border-left: 3px solid #0078d4;
              color: #666666;
            }
            pre, code {
              font-family: Consolas, 'Courier New', monospace;
              font-size: 13px;
              background-color: #f5f5f5;
              border-radius: 3px;
            }
            pre { padding: 12px; overflow-x: auto; white-space: pre-wrap; word-wrap: break-word; }
            code { padding: 2px 4px; }
            ul, ol { margin: 0 0 1em 0; padding-left: 24px; }
            li { margin-bottom: 4px; }
            hr { border: none; border-top: 1px solid #e0e0e0; margin: 1em 0; }
            @media (max-width: 600px) {
              body { padding: 12px; font-size: 13px; }
              table { width: 100% !important; }
              td, th { display: block; width: 100%; }
            }
          </style>
        </head>
        <body>${bodyHtml}</body>
        </html>
      `;
      
      doc.open();
      doc.write(htmlContent);
      doc.close();
      
      const resizeIframe = () => {
        try {
          if (iframe.contentDocument?.body) {
            const height = iframe.contentDocument.body.scrollHeight;
            iframe.style.height = `${Math.min(height + 32, 600)}px`;
          }
        } catch (e) {
          // Ignore cross-origin errors
        }
      };
      
      const timeoutId = setTimeout(resizeIframe, 100);
      // Re-resize after images load (when enabled)
      const timeoutId2 = imagesLoaded ? setTimeout(resizeIframe, 1500) : null;
      iframe.onload = resizeIframe;
      
      return () => {
        clearTimeout(timeoutId);
        if (timeoutId2) clearTimeout(timeoutId2);
        iframe.onload = null;
      };
    }
  }, [email?.body_html, imagesLoaded]);
  
  // Reset images loaded state when email changes
  useEffect(() => {
    setImagesLoaded(false);
  }, [email?.id]);
  
  if (!email?.body_html) {
    return (
      <div className="p-4 text-gray-600 whitespace-pre-wrap font-sans">
        {email?.body_preview || 'No content available'}
      </div>
    );
  }
  
  return (
    <div>
      {hasExternalImages && !imagesLoaded && (
        <div data-testid="email-images-blocked-banner" className="flex items-center gap-2 px-3 py-2 mb-1 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>External images are blocked for security.</span>
          <Button
            data-testid="load-external-images-btn"
            variant="outline"
            size="sm"
            className="ml-auto h-7 text-xs border-amber-300 hover:bg-amber-100"
            onClick={() => setImagesLoaded(true)}
          >
            Load Images
          </Button>
        </div>
      )}
      {imagesLoaded && hasExternalImages && (
        <div data-testid="email-images-loaded-banner" className="flex items-center gap-2 px-3 py-2 mb-1 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span>External images loaded.</span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-xs hover:bg-green-100"
            onClick={() => setImagesLoaded(false)}
          >
            Block Again
          </Button>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Email Content"
        className="w-full border-0 bg-white"
        style={{ minHeight: '200px', height: '400px' }}
        sandbox={imagesLoaded ? "allow-same-origin allow-popups" : "allow-same-origin"}
      />
    </div>
  );
};

// Sync Progress Modal Component
const SyncProgressModal = ({ isOpen, onClose, mailboxId, mailboxEmail }) => {
  const [status, setStatus] = useState({ status: 'starting', progress: 0, message: 'Starting...' });
  const intervalRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && mailboxId) {
      // Poll for status updates
      const pollStatus = async () => {
        try {
          const response = await api.get(`/mailboxes/${mailboxId}/sync-status`);
          setStatus(response.data);
          
          // Stop polling if completed or error
          if (response.data.status === 'completed' || response.data.status === 'error') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        } catch (error) {
          console.error('Failed to get sync status:', error);
        }
      };
      
      pollStatus();
      intervalRef.current = setInterval(pollStatus, 1000);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [isOpen, mailboxId]);
  
  const getStatusIcon = () => {
    switch (status.status) {
      case 'completed':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-500" />;
      default:
        return <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />;
    }
  };
  
  const getStatusColor = () => {
    switch (status.status) {
      case 'completed':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-blue-500';
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Syncing {mailboxEmail}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6 space-y-6">
          {/* Status Icon */}
          <div className="flex justify-center">
            {getStatusIcon()}
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Progress</span>
              <span>{status.progress}%</span>
            </div>
            <Progress value={status.progress} className={`h-2 ${getStatusColor()}`} />
          </div>
          
          {/* Status Message */}
          <div className="text-center">
            <p className="text-sm text-gray-600 truncate px-4">{status.message}</p>
          </div>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{status.imported || 0}</p>
              <p className="text-xs text-gray-500">Imported</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{status.total || 0}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
          
          {/* Close button when done */}
          {(status.status === 'completed' || status.status === 'error') && (
            <div className="flex justify-center">
              <Button onClick={onClose}>
                {status.status === 'completed' ? 'Done' : 'Close'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Office365Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [mailboxes, setMailboxes] = useState([]);
  const [metadataRefreshing, setMetadataRefreshing] = useState({});
  const [metadataStatus, setMetadataStatus] = useState({});
  const metadataRequests = useRef(new Set());
  const [emails, setEmails] = useState([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [outboundEmails, setOutboundEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [repairingImages, setRepairingImages] = useState(false);
  const [repairStatus, setRepairStatus] = useState(null);
  const [showRepairHistory, setShowRepairHistory] = useState(false);
  const [tick, setTick] = useState(0); // Forces re-render for countdown updates
  
  // Pagination and sorting state
  const [emailPage, setEmailPage] = useState(0);
  const [emailLimit, setEmailLimit] = useState(25);
  const [emailSortBy, setEmailSortBy] = useState('received_date');
  const [emailSortOrder, setEmailSortOrder] = useState('desc');
  const [emailSearch, setEmailSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Email detail modal
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  
  // Sync progress modal
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncingMailbox, setSyncingMailbox] = useState(null);
  
  // Creating ticket state
  const [creatingTicket, setCreatingTicket] = useState(false);
  
  // Sent email viewer state
  const [sentEmailDialogOpen, setSentEmailDialogOpen] = useState(false);
  const [selectedSentEmail, setSelectedSentEmail] = useState(null);
  
  // Schedule configuration state
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleData, setScheduleData] = useState({
    auto_sync_enabled: false,
    sync_interval_minutes: 60
  });
  
  // Sync activity log state
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLogsLoading, setSyncLogsLoading] = useState(false);
  const [syncNotifyEnabled, setSyncNotifyEnabled] = useState(true);
  const [newTicketNotifyEnabled, setNewTicketNotifyEnabled] = useState(true);
  
  // Mailbox connection confirmation state
  const [selectMailboxOpen, setSelectMailboxOpen] = useState(false);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [confirmingConnection, setConfirmingConnection] = useState(false);

  const loadMailboxes = async () => {
    try {
      const response = await api.get('/mailboxes');
      setMailboxes(response.data);
    } catch (error) {
      console.error('Failed to load mailboxes:', error);
    }
  };
  
  const loadSyncLogs = async () => {
    setSyncLogsLoading(true);
    try {
      const response = await api.get('/sync-logs?limit=50');
      setSyncLogs(response.data);
    } catch (error) {
      console.error('Failed to load sync logs:', error);
    } finally {
      setSyncLogsLoading(false);
    }
  };
  
  const loadNotificationSettings = async () => {
    try {
      const [syncRes, ticketRes] = await Promise.all([
        api.get('/notification-settings?event=sync_failure').catch(() => ({ data: { enabled: true } })),
        api.get('/notification-settings?event=new_ticket').catch(() => ({ data: { enabled: true } })),
      ]);
      setSyncNotifyEnabled(syncRes.data.enabled !== false);
      setNewTicketNotifyEnabled(ticketRes.data.enabled !== false);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };
  
  const toggleSyncNotifications = async () => {
    const newVal = !syncNotifyEnabled;
    setSyncNotifyEnabled(newVal);
    try {
      await api.put('/notification-settings/sync_failure', { event: 'sync_failure', enabled: newVal });
      toast.success(newVal ? 'Sync failure alerts enabled' : 'Sync failure alerts disabled');
    } catch (error) {
      setSyncNotifyEnabled(!newVal);
      toast.error('Failed to update notification settings');
    }
  };
  
  const toggleNewTicketNotifications = async () => {
    const newVal = !newTicketNotifyEnabled;
    setNewTicketNotifyEnabled(newVal);
    try {
      await api.put('/notification-settings/new_ticket', { event: 'new_ticket', enabled: newVal });
      toast.success(newVal ? 'New ticket alerts enabled' : 'New ticket alerts disabled');
    } catch (error) {
      setNewTicketNotifyEnabled(!newVal);
      toast.error('Failed to update notification settings');
    }
  };

  const loadEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: emailPage * emailLimit,
        limit: emailLimit,
        sort_by: emailSortBy,
        sort_order: emailSortOrder
      });
      
      if (emailSearch) {
        params.append('search', emailSearch);
      }
      
      const response = await api.get(`/emails?${params.toString()}`);
      setEmails(response.data.items || []);
      setEmailsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load emails:', error);
      if (Array.isArray(error.response?.data)) {
        setEmails(error.response.data);
        setEmailsTotal(error.response.data.length);
      }
    }
  }, [emailPage, emailLimit, emailSortBy, emailSortOrder, emailSearch]);

  const loadOutboundEmails = async () => {
    try {
      const response = await api.get('/outbound-emails');
      setOutboundEmails(response.data);
    } catch (error) {
      console.error('Failed to load outbound emails:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadMailboxes(),
        loadEmails(),
        loadOutboundEmails(),
        loadSyncLogs(),
        loadNotificationSettings(),
        loadRepairStatus()
      ]);
    } catch (error) {
      console.error('Office 365 load error:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to view Office 365 settings');
      } else {
        toast.error('Failed to load Office 365 data.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh mailbox list every 15 seconds to keep next-sync countdown accurate
    const refreshInterval = setInterval(() => {
      loadMailboxes();
    }, 15000);
    
    // Tick every 5 seconds to update countdown display without re-fetching
    const tickInterval = setInterval(() => {
      setTick(t => t + 1);
    }, 5000);
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_pending')) {
      const pendingId = params.get('pending_id');
      const email = decodeURIComponent(params.get('email') || '');
      const displayName = decodeURIComponent(params.get('display_name') || '');
      setPendingConnection({ id: pendingId, email, display_name: displayName });
      setSelectMailboxOpen(true);
      window.history.replaceState({}, '', '/office365');
    } else if (params.get('oauth_success')) {
      toast.success('Office 365 mailbox connected successfully!');
      loadData();
      window.history.replaceState({}, '', '/office365');
    } else if (params.get('oauth_error')) {
      toast.error('Failed to connect Office 365 mailbox');
      window.history.replaceState({}, '', '/office365');
    }
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(tickInterval);
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      loadEmails();
    }
  }, [emailPage, emailLimit, emailSortBy, emailSortOrder, emailSearch, loadEmails, loading]);

  const handleConnectMailbox = async () => {
    setConnecting(true);
    try {
      const response = await api.get('/oauth/authorize');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Office 365 connection');
      setConnecting(false);
    }
  };
  
  const handleConfirmMailbox = async () => {
    if (!pendingConnection) return;
    
    setConfirmingConnection(true);
    try {
      await api.post('/mailboxes/confirm', {
        pending_id: pendingConnection.id,
        email: pendingConnection.email,
        display_name: pendingConnection.display_name,
      });
      toast.success(`Mailbox ${pendingConnection.email} connected successfully!`);
      setSelectMailboxOpen(false);
      setPendingConnection(null);
      loadMailboxes();
    } catch (error) {
      toast.error('Failed to connect mailbox: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setConfirmingConnection(false);
    }
  };
  
  const handleCancelPending = async () => {
    if (pendingConnection) {
      try {
        await api.delete(`/mailboxes/pending/${pendingConnection.id}`);
      } catch {}
    }
    setSelectMailboxOpen(false);
    setPendingConnection(null);
  };

  const handleDisconnect = async (mailboxId) => {
    if (!window.confirm('Are you sure you want to disconnect this mailbox?')) return;
    
    try {
      await api.delete(`/mailboxes/${mailboxId}`);
      toast.success('Mailbox disconnected');
      loadMailboxes();
    } catch (error) {
      toast.error('Failed to disconnect mailbox');
    }
  };

  const handleSyncMailbox = async (mailbox) => {
    try {
      setSyncingMailbox(mailbox);
      setSyncModalOpen(true);
      await api.post(`/mailboxes/${mailbox.id}/sync`);
    } catch (error) {
      toast.error('Failed to start email sync');
      setSyncModalOpen(false);
    }
  };

  const handleRefreshMetadata = async (mailbox) => {
    if (!isAdmin || mailbox.metadata_refresh_available === false || metadataRequests.current.has(mailbox.id)) return;
    metadataRequests.current.add(mailbox.id);
    setMetadataRefreshing((previous) => ({ ...previous, [mailbox.id]: true }));
    setMetadataStatus((previous) => ({ ...previous, [mailbox.id]: null }));
    try {
      const { data } = await api.post(`/mailboxes/${mailbox.id}/refresh-metadata`);
      if (!data.mailbox || data.mailbox.id !== mailbox.id) throw new Error('Unexpected mailbox response');
      // Polling may have loaded newer scheduling or run details while Graph was
      // responding. Merge only the identity metadata changed by this request.
      const metadata = {};
      for (const field of ['display_name', 'user_principal_name', 'provider_email', 'metadata_refreshed_at']) {
        if (Object.prototype.hasOwnProperty.call(data.mailbox, field)) metadata[field] = data.mailbox[field];
      }
      setMailboxes((previous) => previous.map((saved) => saved.id === mailbox.id ? { ...saved, ...metadata } : saved));
      setMetadataStatus((previous) => ({ ...previous, [mailbox.id]: { message: 'Saved mailbox metadata refreshed.' } }));
      toast.success('Saved mailbox metadata refreshed');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Could not refresh mailbox metadata. Please try again.';
      setMetadataStatus((previous) => ({ ...previous, [mailbox.id]: { message, error: true } }));
      toast.error(message);
    } finally {
      metadataRequests.current.delete(mailbox.id);
      setMetadataRefreshing((previous) => ({ ...previous, [mailbox.id]: false }));
    }
  };

  const handleRepairImages = async () => {
    setRepairingImages(true);
    try {
      const res = await api.post('/settings/repair-cid-images');
      const d = res.data || {};
      if ((d.total_found || 0) === 0) {
        toast.success('No emails with unresolved embedded images found');
      } else {
        toast.success(d.message || `Repaired ${d.fixed || 0} of ${d.total_found || 0} emails`);
      }
      loadRepairStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repair embedded images');
    } finally {
      setRepairingImages(false);
    }
  };

  const loadRepairStatus = async () => {
    try {
      const res = await api.get('/settings/repair-cid-images/status');
      setRepairStatus(res.data && res.data.last_run ? res.data : null);
    } catch (error) {
      // non-blocking
    }
  };
  
  const handleSyncComplete = () => {
    setSyncModalOpen(false);
    setSyncingMailbox(null);
    loadEmails();
    loadMailboxes();
    loadSyncLogs();
  };

  const handleEmailClick = async (email) => {
    setLoadingEmail(true);
    setEmailModalOpen(true);
    try {
      const response = await api.get(`/emails/${email.id || email.message_id}`);
      setSelectedEmail(response.data);
    } catch (error) {
      console.error('Failed to load email:', error);
      setSelectedEmail(email);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleCreateTicket = async (email) => {
    setCreatingTicket(true);
    try {
      const response = await api.post(`/emails/${email.id || email.message_id}/create-ticket`);
      toast.success(`Ticket ${response.data.ticket_number} created successfully!`);
      
      // Update the selected email with the new ticket info
      setSelectedEmail(prev => ({
        ...prev,
        ticket_id: response.data.ticket_id,
        ticket_number: response.data.ticket_number
      }));
      
      // Reload emails to update the list
      loadEmails();
    } catch (error) {
      console.error('Failed to create ticket:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create ticket');
      }
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setEmailSearch(searchInput);
    setEmailPage(0);
  };

  const handleSortChange = (field) => {
    if (emailSortBy === field) {
      setEmailSortOrder(emailSortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setEmailSortBy(field);
      setEmailSortOrder('desc');
    }
    setEmailPage(0);
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };
  
  const handleViewSentEmail = (email) => {
    setSelectedSentEmail(email);
    setSentEmailDialogOpen(true);
  };
  
  const handleScheduleEdit = (mailbox) => {
    setEditingSchedule(mailbox.id);
    setScheduleData({
      auto_sync_enabled: mailbox.auto_sync_enabled || false,
      sync_interval_minutes: mailbox.sync_interval_minutes || 60,
      move_after_import: mailbox.move_after_import !== false,
      move_to_folder: mailbox.move_to_folder || 'CipherOne Processed'
    });
  };
  
  const handleScheduleSave = async (mailboxId) => {
    try {
      await api.put(`/mailboxes/${mailboxId}/schedule`, {
        auto_sync_enabled: scheduleData.auto_sync_enabled,
        sync_interval_minutes: scheduleData.sync_interval_minutes
      });
      await api.put(`/mailboxes/${mailboxId}/folder-settings`, {
        move_after_import: scheduleData.move_after_import,
        move_to_folder: scheduleData.move_to_folder
      });
      toast.success('Settings updated successfully');
      setEditingSchedule(null);
      loadMailboxes();
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    }
  };
  
  const handleScheduleCancel = () => {
    setEditingSchedule(null);
    setScheduleData({
      auto_sync_enabled: false,
      sync_interval_minutes: 60
    });
  };
  
  // Test email states
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailData, setTestEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    mailbox_id: ''
  });
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setSendingTestEmail(true);
    try {
      const mailbox = mailboxes.find(m => m.id === testEmailData.mailbox_id);
      const subject = 'CipherOne Test Email';
      const body_html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #0078d4 0%, #005a9e 100%); color: white; padding: 30px; border-radius: 8px 8px 0 0; text-align: center;">
            <h1 style="margin: 0; font-size: 28px;">CipherOne MSP</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9;">Email Integration Test</p>
          </div>
          
          <div style="background: white; padding: 30px; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 8px 8px;">
            <h2 style="color: #0078d4; margin-top: 0;">✓ Email Test Successful!</h2>
            <p style="color: #333; line-height: 1.6;">
              This is a test email sent from your CipherOne MSP Portal. If you're reading this, 
              your Office 365 email integration is configured correctly and working properly.
            </p>
            
            <div style="background: #f5f5f5; padding: 15px; border-radius: 6px; margin: 20px 0;">
              <p style="margin: 0; color: #666; font-size: 13px;"><strong>Sent from mailbox:</strong> ${mailbox?.user_email || 'Unknown'}</p>
              <p style="margin: 5px 0 0 0; color: #666; font-size: 13px;"><strong>Sent at:</strong> ${new Date().toLocaleString()}</p>
              <p style="margin: 5px 0 0 0; color: #666; font-size: 13px;"><strong>System:</strong> CipherOne MSP</p>
            </div>
            
            <div style="background: #e8f5e9; border-left: 4px solid #4caf50; padding: 15px; border-radius: 4px;">
              <p style="margin: 0; color: #2e7d32; font-size: 14px;">
                <strong>✓ Your Office 365 integration is working correctly!</strong><br />
                You can now send emails, ticket notifications, and automated messages through CipherOne.
              </p>
            </div>
          </div>
          
          <div style="text-align: center; padding: 20px; color: #999; font-size: 12px;">
            <p style="margin: 0;">Sent via CipherOne MSP Portal - Mathews IT Services</p>
          </div>
        </div>
      `;
      
      const payload = {
        mailbox_id: testEmailData.mailbox_id,
        recipient_email: testEmailData.recipient_email,
        recipient_name: testEmailData.recipient_name || testEmailData.recipient_email.split('@')[0],
        subject: subject,
        body_html: body_html
      };
      
      await api.post('/emails/send', payload);
      
      toast.success(`Test email sent successfully to ${testEmailData.recipient_email}!`);
      setTestEmailDialogOpen(false);
      setTestEmailData({
        recipient_email: '',
        recipient_name: '',
        mailbox_id: ''
      });
      loadData(); // Reload to show in sent emails
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const getSyncIntervalLabel = (minutes) => {
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes === 60) return '1 hour';
    if (minutes === 120) return '2 hours';
    if (minutes === 240) return '4 hours';
    return `${minutes} minutes`;
  };

  // Honest per-mailbox health lives in utils/mailboxHealth (unit-tested).


  const totalPages = Math.ceil(emailsTotal / emailLimit);

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <div className="text-lg text-gray-600">Loading Office 365 settings...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-4 sm:space-y-6" data-testid="office365-settings-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Office 365 Email</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Connect mailboxes and manage email-to-ticket automation</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleRepairImages}
            disabled={repairingImages}
            data-testid="repair-embedded-images-btn"
            title="Re-fetch embedded (inline) images for previously imported emails"
          >
            {repairingImages ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wrench className="h-4 w-4 mr-2" />}
            {repairingImages ? 'Repairing...' : 'Repair Embedded Images'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setTestEmailDialogOpen(true)}
            disabled={mailboxes.length === 0}
          >
            <Mail className="h-4 w-4 mr-2" />
            Test Email Sending
          </Button>
          <Button onClick={handleConnectMailbox} disabled={connecting}>
            <Plus className="h-4 w-4 mr-2" />
            {connecting ? 'Connecting...' : 'Connect Office 365 Mailbox'}
          </Button>
        </div>
      </div>

      {repairStatus && (
        <div data-testid="repair-status-card" className="bg-gray-50 border rounded-md px-3 py-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Wrench className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span>
              Last embedded-image repair{' '}
              <span className="font-medium">{repairStatus.trigger === 'auto' ? '(auto)' : '(manual)'}</span>:{' '}
              {formatDistanceToNow(new Date(repairStatus.last_run), { addSuffix: true })} ·{' '}
              <span className="font-medium">{repairStatus.fixed || 0}</span> fixed
              {repairStatus.total_found ? ` of ${repairStatus.total_found} found` : ''}
            </span>
            {Array.isArray(repairStatus.history) && repairStatus.history.length > 0 && (
              <button
                type="button"
                data-testid="repair-history-toggle"
                onClick={() => setShowRepairHistory((v) => !v)}
                className="ml-auto text-blue-600 hover:text-blue-800 font-medium"
              >
                {showRepairHistory ? 'Hide history' : 'View recent runs'}
              </button>
            )}
          </div>
          {showRepairHistory && Array.isArray(repairStatus.history) && (
            <ul data-testid="repair-history-list" className="mt-2 pt-2 border-t space-y-1">
              {[...repairStatus.history].reverse().map((h, i) => (
                <li key={i} data-testid="repair-history-item" className="flex items-center gap-2 text-xs text-gray-500">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${h.trigger === 'auto' ? 'bg-gray-400' : 'bg-blue-500'}`} />
                  <span className="w-14 shrink-0">{h.trigger === 'auto' ? 'auto' : 'manual'}</span>
                  <span className="flex-1">{formatDistanceToNow(new Date(h.ts), { addSuffix: true })}</span>
                  <span><span className="font-medium text-gray-700">{h.fixed || 0}</span> fixed / {h.total_found || 0} found{h.errors ? ` · ${h.errors} err` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Tabs defaultValue="mailboxes" className="space-y-4">
        <TabsList>
          <TabsTrigger value="mailboxes">Connected Mailboxes</TabsTrigger>
          <TabsTrigger value="inbox">Imported Emails ({emailsTotal})</TabsTrigger>
          <TabsTrigger value="sent">Sent Emails</TabsTrigger>
          <TabsTrigger value="activity" onClick={() => { if (syncLogs.length === 0) loadSyncLogs(); }}>
            <Activity className="h-3.5 w-3.5 mr-1" />
            Sync Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mailboxes">
          <Card>
            <CardHeader>
              <CardTitle>Connected Mailboxes</CardTitle>
              <CardDescription>Manage Office 365 mailbox connections</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 rounded-lg border border-cyan-200 bg-cyan-50 dark:bg-cyan-950/30 dark:border-cyan-800 p-3" data-testid="mailbox-identity-note">
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 mt-0.5 text-cyan-700 dark:text-cyan-300 shrink-0" />
                  <div className="text-xs leading-relaxed text-cyan-900 dark:text-cyan-100">
                    <p className="font-semibold">Branding and public contact identity are configured separately</p>
                    <p className="mt-0.5">Customer-facing branding (company name, logo and public support address) is managed under Email Branding and is display-only. It does not change which mailbox actually sends mail.</p>
                    <p className="mt-0.5 text-cyan-700 dark:text-cyan-300">The rows below show each connection's <strong>saved mailbox metadata</strong>. The authoritative sending identity is the Microsoft 365 account the connection was authorized with &mdash; a branding reply-to address is not proof of Send-As permission.</p>
                  </div>
                </div>
              </div>
              {mailboxes.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">No mailboxes connected yet</p>
                  <Button onClick={handleConnectMailbox}>
                    <Plus className="h-4 w-4 mr-2" />
                    Connect Your First Mailbox
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mailbox (saved)</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Health</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Last Recorded Run</TableHead>
                      <TableHead>Next Sync</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mailboxes.map((mailbox) => (
                      <TableRow key={mailbox.id}>
                        <TableCell className="font-medium">{mailbox.user_email}</TableCell>
                        <TableCell>
                          <p>{mailbox.display_name}</p>
                          {(mailbox.user_principal_name || mailbox.provider_email) && (
                            <p className="mt-1 text-xs text-muted-foreground break-all">
                              Microsoft account (saved): {mailbox.user_principal_name || mailbox.provider_email}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          {mailbox.is_active ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <CheckCircle className="h-3 w-3 mr-1 inline" />Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                              <XCircle className="h-3 w-3 mr-1 inline" />Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const h = deriveMailboxHealth(mailbox);
                            return (
                              <Badge className={`${h.cls} hover:${h.cls}`} title={h.title} data-testid={`mailbox-health-${mailbox.id}`}>
                                {h.label}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {editingSchedule === mailbox.id ? (
                            <div className="flex flex-col gap-2 min-w-[200px]">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={scheduleData.auto_sync_enabled}
                                  onCheckedChange={(checked) => setScheduleData({...scheduleData, auto_sync_enabled: checked})}
                                />
                                <span className="text-sm">Auto-sync enabled</span>
                              </div>
                              {scheduleData.auto_sync_enabled && (
                                <Select
                                  value={scheduleData.sync_interval_minutes.toString()}
                                  onValueChange={(v) => setScheduleData({...scheduleData, sync_interval_minutes: parseInt(v)})}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">Every 1 minute</SelectItem>
                                    <SelectItem value="5">Every 5 minutes</SelectItem>
                                    <SelectItem value="15">Every 15 minutes</SelectItem>
                                    <SelectItem value="30">Every 30 minutes</SelectItem>
                                    <SelectItem value="60">Every hour</SelectItem>
                                    <SelectItem value="120">Every 2 hours</SelectItem>
                                    <SelectItem value="240">Every 4 hours</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              
                              {/* Folder Settings */}
                              <div className="border-t pt-2 mt-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={scheduleData.move_after_import}
                                    onCheckedChange={(checked) => setScheduleData({...scheduleData, move_after_import: checked})}
                                  />
                                  <span className="text-sm">Move emails after import</span>
                                </div>
                                {scheduleData.move_after_import && (
                                  <Input
                                    value={scheduleData.move_to_folder}
                                    onChange={(e) => setScheduleData({...scheduleData, move_to_folder: e.target.value})}
                                    placeholder="Folder name"
                                    className="h-8 text-sm"
                                  />
                                )}
                              </div>

                              <div className="flex gap-1">
                                <Button size="sm" onClick={() => handleScheduleSave(mailbox.id)}>
                                  <CheckCircle className="h-3 w-3 mr-1" />Save
                                </Button>
                                <Button size="sm" variant="ghost" onClick={handleScheduleCancel}>
                                  <XCircle className="h-3 w-3 mr-1" />Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {mailbox.auto_sync_enabled ? (
                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {getSyncIntervalLabel(mailbox.sync_interval_minutes || 60)}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500">
                                  Manual only
                                </Badge>
                              )}
                              {mailbox.move_after_import !== false && mailbox.move_to_folder && (
                                <Badge variant="outline" className="text-neutral-500 text-xs">
                                  → {mailbox.move_to_folder}
                                </Badge>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleScheduleEdit(mailbox)}
                              >
                                <Settings className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <MailboxLastRun mailbox={mailbox} />
                        </TableCell>
                        <TableCell>
                          {mailbox.auto_sync_enabled && mailbox.next_sync_at ? (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Clock className="h-3 w-3" />
                              {(() => {
                                // Ensure the date is parsed as UTC by appending 'Z' if missing
                                const rawDate = mailbox.next_sync_at;
                                const utcDate = rawDate.endsWith('Z') ? rawDate : rawDate + 'Z';
                                const nextSync = new Date(utcDate);
                                const now = new Date();
                                const diffMs = nextSync - now;
                                if (diffMs <= 0) return 'syncing soon...';
                                const diffSec = Math.floor(diffMs / 1000);
                                if (diffSec < 60) return `in ${diffSec}s`;
                                const diffMin = Math.floor(diffSec / 60);
                                if (diffMin < 60) return `in ${diffMin} min`;
                                const diffHr = Math.floor(diffMin / 60);
                                const remMin = diffMin % 60;
                                return `in ${diffHr}h ${remMin}m`;
                              })()}
                            </div>
                          ) : mailbox.auto_sync_enabled ? (
                            <div className="text-sm text-gray-400">
                              Every {mailbox.sync_interval_minutes === 1 ? '1 min' : `${mailbox.sync_interval_minutes} min`}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {isAdmin && (
                              <Button size="sm" variant="outline"
                                onClick={() => handleRefreshMetadata(mailbox)}
                                disabled={metadataRefreshing[mailbox.id] || mailbox.metadata_refresh_available === false}
                                title="Read the saved Microsoft account's current profile metadata"
                                data-testid={`mailbox-refresh-metadata-${mailbox.id}`}>
                                <RefreshCw className={`h-3 w-3 mr-1 ${metadataRefreshing[mailbox.id] ? 'animate-spin' : ''}`} />
                                {metadataRefreshing[mailbox.id] ? 'Refreshing metadata…' : 'Refresh metadata'}
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleSyncMailbox(mailbox)}>
                              <RefreshCw className="h-3 w-3 mr-1" />Sync Now
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDisconnect(mailbox.id)}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                          {isAdmin && mailbox.metadata_refresh_available === false && (
                            <p className="mt-2 max-w-xs text-xs text-muted-foreground">
                              {mailbox.metadata_refresh_blocked_reason || 'Provider identity must be verified before metadata can be refreshed.'}
                            </p>
                          )}
                          {isAdmin && metadataStatus[mailbox.id] && (
                            <p role={metadataStatus[mailbox.id].error ? 'alert' : 'status'}
                              className={`mt-2 max-w-xs text-xs ${metadataStatus[mailbox.id].error ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                              {metadataStatus[mailbox.id].message}
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inbox">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div>
                  <CardTitle>Imported Emails</CardTitle>
                  <CardDescription>Emails synced from Office 365 mailboxes - {emailsTotal} total</CardDescription>
                </div>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <Input
                    placeholder="Search emails..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-48 sm:w-64"
                  />
                  <Button type="submit" variant="outline" size="icon">
                    <Search className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </CardHeader>
            <CardContent>
              {emails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No emails imported yet</p>
                  <p className="text-sm mt-1">Connect a mailbox and sync to import emails</p>
                </div>
              ) : (
                <>
                  <div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSortChange('sender_email')}
                          >
                            <div className="flex items-center gap-1">
                              From
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSortChange('subject')}
                          >
                            <div className="flex items-center gap-1">
                              Subject
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 w-40"
                            onClick={() => handleSortChange('received_date')}
                          >
                            <div className="flex items-center gap-1">
                              Date
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead className="w-36">Ticket</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emails.map((email) => (
                          <TableRow 
                            key={email.id || email.message_id} 
                            className="cursor-pointer hover:bg-blue-50"
                            onClick={() => handleEmailClick(email)}
                          >
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{email.sender_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500 truncate max-w-[200px]">{email.sender_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {email.has_attachments && <Paperclip className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                                <span className="line-clamp-1 text-sm">{email.subject || '(No Subject)'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {email.received_date ? formatInCST(email.received_date, 'MMM d, h:mm a') : '-'}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {email.ticket_id ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs font-medium bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                                  onClick={() => handleViewTicket(email.ticket_id)}
                                >
                                  <Ticket className="h-3 w-3 mr-1" />
                                  {email.ticket_number || 'View'}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs font-medium bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                  onClick={() => handleEmailClick(email)}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Create
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Rows per page:</span>
                      <Select value={emailLimit.toString()} onValueChange={(v) => { setEmailLimit(parseInt(v)); setEmailPage(0); }}>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">
                        {emailsTotal > 0 ? `${emailPage * emailLimit + 1} - ${Math.min((emailPage + 1) * emailLimit, emailsTotal)} of ${emailsTotal}` : '0 results'}
                      </span>
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          onClick={() => setEmailPage(p => Math.max(0, p - 1))}
                          disabled={emailPage === 0}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          onClick={() => setEmailPage(p => p + 1)}
                          disabled={emailPage >= totalPages - 1}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sent">
          <Card>
            <CardHeader>
              <CardTitle>Sent Emails</CardTitle>
              <CardDescription>Emails sent from CipherOne</CardDescription>
            </CardHeader>
            <CardContent>
              {outboundEmails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No emails sent yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {outboundEmails.map((email) => (
                    <div 
                      key={email.id} 
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleViewSentEmail(email)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold">{email.subject}</h4>
                          <p className="text-sm text-gray-600">To: {email.recipient_name} ({email.recipient_email})</p>
                          <p className="text-xs text-gray-500 mt-1">Type: {email.email_type}</p>
                          {email.related_ticket_id && (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs mt-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewTicket(email.related_ticket_id);
                              }}
                            >
                              <Ticket className="h-3 w-3 mr-1" />
                              View Related Ticket
                            </Button>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{formatInCST(email.sent_at || email.created_at, 'MMM d, h:mm a')}</p>
                          {email.status === 'sent' ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 mt-1">
                              <CheckCircle className="h-3 w-3 mr-1 inline" />Sent
                            </Badge>
                          ) : email.status === 'failed' ? (
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100 mt-1">
                              <XCircle className="h-3 w-3 mr-1 inline" />Failed
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 mt-1">
                              <Clock className="h-3 w-3 mr-1 inline" />Pending
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Sync Activity Log
                  </CardTitle>
                  <CardDescription>Recent email sync runs across all connected mailboxes</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 border rounded-md px-3 py-1.5">
                    <Mail className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-xs text-gray-600">New Ticket Alerts</span>
                    <Switch
                      checked={newTicketNotifyEnabled}
                      onCheckedChange={toggleNewTicketNotifications}
                      data-testid="new-ticket-notify-toggle"
                    />
                  </div>
                  <div className="flex items-center gap-2 border rounded-md px-3 py-1.5">
                    <Bell className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs text-gray-600">Failure Alerts</span>
                    <Switch
                      checked={syncNotifyEnabled}
                      onCheckedChange={toggleSyncNotifications}
                      data-testid="sync-notify-toggle"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={loadSyncLogs} disabled={syncLogsLoading}>
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncLogsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {syncLogsLoading && syncLogs.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : syncLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">No sync activity yet</p>
                  <p className="text-sm text-gray-400 mt-1">Activity will appear here after the first email sync</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Summary stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-900">{syncLogs.length}</p>
                      <p className="text-xs text-gray-500">Total Syncs</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{syncLogs.filter(l => l.status === 'success').length}</p>
                      <p className="text-xs text-gray-500">Successful</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{syncLogs.filter(l => l.status === 'error').length}</p>
                      <p className="text-xs text-gray-500">Failed</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        {syncLogs.reduce((sum, l) => sum + (l.emails_imported || 0), 0)}
                      </p>
                      <p className="text-xs text-gray-500">Emails Imported</p>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="relative">
                    <div className="absolute left-[17px] top-2 bottom-2 w-px bg-gray-200" />
                    <div className="space-y-0">
                      {syncLogs.map((log, index) => {
                        const isSuccess = log.status === 'success';
                        const completedAt = log.completed_at
                          ? (() => {
                              const raw = log.completed_at;
                              return new Date(raw.endsWith('Z') ? raw : raw + 'Z');
                            })()
                          : null;
                        
                        return (
                          <div key={log.id || index} className="relative flex items-start gap-3 py-2 pl-1">
                            {/* Timeline dot */}
                            <div className={`relative z-10 flex-shrink-0 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center mt-0.5 ${
                              isSuccess 
                                ? 'bg-green-100 border-green-400' 
                                : 'bg-red-100 border-red-400'
                            }`}>
                              {isSuccess 
                                ? <CheckCircle className="h-2.5 w-2.5 text-green-600" /> 
                                : <AlertCircle className="h-2.5 w-2.5 text-red-600" />
                              }
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-medium text-sm text-gray-900 truncate">
                                  {log.mailbox_email || 'Unknown mailbox'}
                                </span>
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${
                                  log.trigger === 'auto' 
                                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                                    : 'bg-gray-50 text-gray-600 border-gray-200'
                                }`}>
                                  {log.trigger === 'auto' ? 'Auto' : 'Manual'}
                                </Badge>
                                {isSuccess ? (
                                  <span className="text-xs text-gray-500">
                                    {log.emails_imported > 0 
                                      ? <span className="text-green-600 font-medium">+{log.emails_imported} imported</span>
                                      : 'No new emails'
                                    }
                                    {log.emails_processed > 0 && ` (${log.emails_processed} checked)`}
                                  </span>
                                ) : (
                                  <span className="text-xs text-red-600 truncate max-w-[200px]">
                                    {log.error_message || 'Sync failed'}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {completedAt 
                                  ? `${format(completedAt, 'MMM d, h:mm:ss a')} · ${formatDistanceToNow(completedAt, { addSuffix: true })}`
                                  : 'Unknown time'
                                }
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sync Progress Modal */}
      <SyncProgressModal
        isOpen={syncModalOpen}
        onClose={handleSyncComplete}
        mailboxId={syncingMailbox?.id}
        mailboxEmail={syncingMailbox?.user_email}
      />

      {/* Email Detail Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="w-[95vw] max-w-4xl h-[90vh] max-h-[90vh] p-0 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 border-b bg-gray-50">
            <DialogHeader className="px-4 py-3">
              <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2">
                {loadingEmail ? 'Loading...' : (selectedEmail?.subject || '(No Subject)')}
              </DialogTitle>
            </DialogHeader>
          </div>
          
          {loadingEmail ? (
            <div className="flex items-center justify-center py-12 flex-1">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : selectedEmail ? (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-shrink-0 px-4 py-3 bg-white border-b space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {(selectedEmail.sender_name || selectedEmail.sender_email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {selectedEmail.sender_name || selectedEmail.sender_email?.split('@')[0] || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-500">{selectedEmail.sender_email}</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {selectedEmail.received_date ? formatInCST(selectedEmail.received_date, 'EEE, MMM d, yyyy h:mm a') : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="text-sm space-y-1 pl-13">
                  {selectedEmail.to_recipients && selectedEmail.to_recipients.length > 0 && (
                    <p className="text-gray-600">
                      <span className="text-gray-400 mr-2">To:</span>
                      {selectedEmail.to_recipients.join('; ')}
                    </p>
                  )}
                  {selectedEmail.cc_recipients && selectedEmail.cc_recipients.length > 0 && (
                    <p className="text-gray-600">
                      <span className="text-gray-400 mr-2">Cc:</span>
                      {selectedEmail.cc_recipients.join('; ')}
                    </p>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 items-center pl-13">
                  {selectedEmail.has_attachments && (
                    <Badge variant="outline" className="text-xs bg-gray-100">
                      <Paperclip className="h-3 w-3 mr-1" />
                      Attachments
                    </Badge>
                  )}
                  
                  {selectedEmail.ticket_id ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      onClick={() => {
                        setEmailModalOpen(false);
                        handleViewTicket(selectedEmail.ticket_id);
                      }}
                    >
                      <Ticket className="h-3 w-3 mr-1" />
                      {selectedEmail.ticket_number || 'View Ticket'}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                      onClick={() => handleCreateTicket(selectedEmail)}
                      disabled={creatingTicket}
                    >
                      {creatingTicket ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3 mr-1" />
                      )}
                      {creatingTicket ? 'Creating...' : 'Create Ticket'}
                    </Button>
                  )}
                  
                  {selectedEmail.importance === 'high' && (
                    <Badge className="bg-red-100 text-red-700 text-xs">High Importance</Badge>
                  )}
                </div>
              </div>
              
              <div className="flex-1 overflow-auto bg-white">
                <EmailViewer email={selectedEmail} />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendTestEmail} className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Test your Office 365 email integration</strong><br />
                This will send a test email from your connected mailbox to verify outbound email functionality.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="test_mailbox">Send From Mailbox *</Label>
              <Select
                value={testEmailData.mailbox_id}
                onValueChange={(value) => setTestEmailData({...testEmailData, mailbox_id: value})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a connected mailbox" />
                </SelectTrigger>
                <SelectContent>
                  {mailboxes.filter(m => m.is_active).map((mailbox) => (
                    <SelectItem key={mailbox.id} value={mailbox.id}>
                      {mailbox.user_email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                The test email will be sent from this mailbox
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="test_recipient">Recipient Email *</Label>
              <Input
                id="test_recipient"
                type="email"
                value={testEmailData.recipient_email}
                onChange={(e) => setTestEmailData({...testEmailData, recipient_email: e.target.value})}
                required
                placeholder="your.email@example.com"
              />
              <p className="text-xs text-gray-500">
                Where to send the test email (use your own email to verify)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="test_recipient_name">Recipient Name</Label>
              <Input
                id="test_recipient_name"
                value={testEmailData.recipient_name}
                onChange={(e) => setTestEmailData({...testEmailData, recipient_name: e.target.value})}
                placeholder="Your Name (optional)"
              />
            </div>
            
            <div className="p-3 bg-gray-50 rounded border text-sm">
              <p className="font-semibold mb-1">Test Email Will Include:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Subject: "CipherOne Test Email"</li>
                <li>Sender mailbox information</li>
                <li>Timestamp</li>
                <li>Success confirmation message</li>
              </ul>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTestEmailDialogOpen(false)}
                disabled={sendingTestEmail}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={sendingTestEmail || !testEmailData.mailbox_id || !testEmailData.recipient_email}
              >
                {sendingTestEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending Test Email...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sent Email Viewer Dialog */}
      <Dialog open={sentEmailDialogOpen} onOpenChange={setSentEmailDialogOpen}>
        <DialogContent className="w-[95vw] max-w-4xl h-[90vh] max-h-[90vh] p-0 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 border-b bg-gray-50">
            <DialogHeader className="px-4 py-3">
              <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2">
                {selectedSentEmail?.subject || '(No Subject)'}
              </DialogTitle>
            </DialogHeader>
          </div>
          
          {selectedSentEmail && (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-shrink-0 px-4 py-3 bg-white border-b space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <div>
                        <p className="font-semibold text-gray-900">
                          To: {selectedSentEmail.recipient_name}
                        </p>
                        <p className="text-sm text-gray-500">{selectedSentEmail.recipient_email}</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {selectedSentEmail.sent_at ? formatInCST(selectedSentEmail.sent_at, 'EEE, MMM d, yyyy h:mm a') : 
                         formatInCST(selectedSentEmail.created_at, 'EEE, MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 items-center pl-13">
                  <Badge className={
                    selectedSentEmail.status === 'sent' ? 'bg-green-100 text-green-800' :
                    selectedSentEmail.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }>
                    {selectedSentEmail.status === 'sent' && <CheckCircle className="h-3 w-3 mr-1 inline" />}
                    {selectedSentEmail.status === 'failed' && <XCircle className="h-3 w-3 mr-1 inline" />}
                    {selectedSentEmail.status === 'pending' && <Clock className="h-3 w-3 mr-1 inline" />}
                    {selectedSentEmail.status}
                  </Badge>
                  
                  {selectedSentEmail.email_type && (
                    <Badge variant="outline" className="text-xs">
                      Type: {selectedSentEmail.email_type}
                    </Badge>
                  )}
                  
                  {selectedSentEmail.related_ticket_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      onClick={() => {
                        setSentEmailDialogOpen(false);
                        handleViewTicket(selectedSentEmail.related_ticket_id);
                      }}
                    >
                      <Ticket className="h-3 w-3 mr-1" />
                      View Related Ticket
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                  
                  {selectedSentEmail.error_message && (
                    <Badge className="bg-red-100 text-red-700 text-xs">
                      Error: {selectedSentEmail.error_message}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex-1 overflow-auto bg-white p-4">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedSentEmail.body_html) }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mailbox Connection Confirmation Dialog */}
      <Dialog open={selectMailboxOpen} onOpenChange={(open) => { if (!open) handleCancelPending(); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Connect Mailbox</DialogTitle>
            <DialogDescription>
              Confirm the mailbox you'd like to connect to CipherOne.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2">
            {pendingConnection && (
              <div className="p-4 border-2 border-blue-500 bg-blue-50 rounded-lg" data-testid="detected-mailbox-option">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{pendingConnection.display_name || 'Office 365 Account'}</p>
                    <p className="text-sm text-gray-600">{pendingConnection.email}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  This mailbox was authenticated directly. Emails sent to this address will be imported into CipherOne.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelPending} data-testid="cancel-mailbox-btn">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMailbox}
              disabled={confirmingConnection}
              data-testid="confirm-mailbox-btn"
            >
              {confirmingConnection ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Connect Mailbox
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
